import React from "react";
import PropTypes from "prop-types";
import { Box, Chip, InputAdornment, InputLabel, Stack, TextField, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import { Controller } from "react-hook-form";

// `key` is the suffix appended to the pay-type prefix to build the override field name
// (`${prefix}Fringe${key}`, e.g. unionOverTimeFringeApprenticeTraining). `straightField` is the
// straight-time baseline shown beneath each override input.
const FRINGE_CATEGORIES = [
  { key: "HealthAndWelfare", label: "Health & Welfare (H&W)", short: "H&W", straightField: "unionStraightTimeFringeHealthAndWelfare" },
  { key: "Pension", label: "Pension (PENS)", short: "Pension", straightField: "unionStraightTimeFringePension" },
  { key: "Annuity", label: "Annuity (ANN)", short: "Annuity", straightField: "unionStraightTimeFringeAnnuity" },
  {
    key: "ApprenticeTraining",
    label: "Apprentice Training (APPR)",
    short: "Apprentice Training",
    straightField: "unionStraightTimeFringeApprenticeTraining"
  },
  { key: "Vacation", label: "Vacation/Sick (VAC)", short: "Vacation/Sick", straightField: "unionStraightTimeFringeVacation" },
  { key: "Other", label: "Other (OTH)", short: "Other", straightField: "unionStraightTimeFringeOther" }
];

const DEFAULT_OVERRIDE_VALUE = "0.00";

const formatStraightValue = value => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return "0.00";
  return num.toFixed(2);
};

// Round half-up to two decimal places using string-based math to avoid IEEE 754 ties.
// Returns null when the input cannot be coerced to a number — callers should fall back.
const roundHalfUpTo2 = rawValue => {
  if (rawValue === null || rawValue === undefined) return null;
  const str = String(rawValue).trim();
  if (str === "" || str === "-" || str === "." || str === "-.") return null;
  const match = str.match(/^(-?)(\d*)(?:\.(\d*))?$/);
  if (!match) return null;
  const sign = match[1] === "-" ? -1 : 1;
  const intPart = match[2] === "" ? "0" : match[2];
  const decPart = match[3] || "";
  if (match[2] === "" && decPart === "") return null;
  if (decPart.length <= 2) {
    const num = parseFloat(`${sign === -1 ? "-" : ""}${intPart}.${decPart.padEnd(2, "0") || "00"}`);
    return Number.isNaN(num) ? null : num.toFixed(2);
  }
  const truncated = parseFloat(`${intPart}.${decPart.slice(0, 2)}`);
  const roundDigit = parseInt(decPart[2], 10);
  const rounded = roundDigit >= 5 ? truncated + 0.01 : truncated;
  return (sign * rounded).toFixed(2);
};

const handleNumericKeyDown = e => {
  const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete", ".", "Home", "End"];
  if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
  if (!/\d/.test(e.key) && !allowedKeys.includes(e.key)) e.preventDefault();
  if (e.key === "." && e.target.value.includes(".")) e.preventDefault();
};

const ProjectJobsOvertimeUnionFringeForm = ({
  formConfig,
  defaultFormProps,
  watch,
  setValue,
  addJobPermisison,
  prefix,
  title,
  disabled = false,
  isInvalid = false,
  onActivate
}) => {
  const isUnion = watch("isUnion");
  const interactionEnabled = isUnion && !disabled;
  const isReadMode = isUnion && disabled;
  const handleActivate = () => {
    if (typeof onActivate === "function") onActivate();
  };

  const fieldName = categoryKey => `${prefix}Fringe${categoryKey}`;

  // Each override category is gated through its registered perKey (= the built field name).
  const isCategoryViewable = categoryKey => !!addJobPermisison?.fieldPerObj?.[fieldName(categoryKey)]?.canView;
  const isCategoryEditable = categoryKey => {
    const p = addJobPermisison?.fieldPerObj?.[fieldName(categoryKey)];
    return !!(p?.canEdit || p?.canAdd);
  };

  const isCategorySelected = categoryKey => {
    const value = watch(fieldName(categoryKey));
    return value !== null && value !== undefined;
  };

  const toggleCategory = categoryKey => {
    const name = fieldName(categoryKey);
    if (isCategorySelected(categoryKey)) {
      setValue(name, null, { shouldDirty: true });
    } else {
      setValue(name, DEFAULT_OVERRIDE_VALUE, { shouldDirty: true });
    }
  };

  const renderInput = (categoryKey, straightField) => {
    const name = fieldName(categoryKey);
    const straightValue = watch(straightField);
    return (
      <Controller
        control={defaultFormProps.control}
        name={name}
        rules={formConfig[name]?.rules}
        render={({ field: { onChange, onBlur, value } }) => (
          <Box>
            {isReadMode ? (
              <Box className="proInputWrapper-read-value" onClick={handleActivate} sx={{ cursor: "pointer", pl: "5px", mt: "-10px", minHeight: 24 }}>
                {value !== null && value !== undefined && value !== "" ? `$${formatStraightValue(value)}` : "-"}
              </Box>
            ) : (
              <TextField
                onKeyDown={handleNumericKeyDown}
                onChange={e => onChange(e.target.value)}
                onBlur={e => {
                  const trimmed = e.target.value.trim();
                  if (trimmed === "") {
                    onChange(DEFAULT_OVERRIDE_VALUE);
                  } else {
                    const rounded = roundHalfUpTo2(trimmed);
                    onChange(rounded ?? DEFAULT_OVERRIDE_VALUE);
                  }
                  onBlur();
                }}
                value={value ?? ""}
                disabled={!isUnion || !isCategoryEditable(categoryKey)}
                variant="outlined"
                fullWidth
                type="number"
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { min: 0, step: "0.01" }
                }}
              />
            )}
            <Typography variant="caption" sx={{ color: "#888", pl: 0.5 }}>
              Straight: ${formatStraightValue(straightValue)}
            </Typography>
          </Box>
        )}
      />
    );
  };

  const borderColor = isInvalid ? "#d32f2f" : "#1976d2";
  const backgroundColor = isInvalid ? "#fff5f5" : "#f5f9ff";
  const titleColor = isInvalid ? "#d32f2f" : "#1976d2";

  return (
    <Box
      sx={{
        border: `1px ${isInvalid ? "solid" : "dashed"} ${borderColor}`,
        borderRadius: "8px",
        p: 2,
        mt: 1,
        backgroundColor
      }}
    >
      <Typography sx={{ color: titleColor, fontWeight: 600, fontSize: 13, letterSpacing: 0.5 }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: "#3C4655", mb: 1.5 }}>
        Select which fringes have a different {prefix === "unionOverTime" ? "overtime" : "double-time"} rate. Unselected fringes will reuse the
        straight-time value.
      </Typography>

      <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
        {FRINGE_CATEGORIES.filter(cat => isCategoryViewable(cat.key)).map(cat => {
          const selected = isCategorySelected(cat.key);
          return (
            <Chip
              key={cat.key}
              label={cat.label}
              icon={selected ? <CheckIcon style={{ fontSize: 16 }} /> : <AddIcon style={{ fontSize: 16 }} />}
              onClick={() => {
                if (interactionEnabled) toggleCategory(cat.key);
                else if (isReadMode) handleActivate();
              }}
              variant={selected ? "filled" : "outlined"}
              color={selected ? "primary" : "default"}
              disabled={!isUnion}
              sx={{
                borderRadius: "999px",
                cursor: interactionEnabled || isReadMode ? "pointer" : "default",
                ...(isReadMode && {
                  opacity: 0.55,
                  filter: "grayscale(0.4)",
                  pointerEvents: "auto",
                  "& .MuiChip-icon": { color: "action.disabled" }
                })
              }}
            />
          );
        })}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" },
          gap: 2
        }}
      >
        {FRINGE_CATEGORIES.filter(cat => isCategoryViewable(cat.key) && isCategorySelected(cat.key)).map(cat => (
          <Box key={cat.key}>
            <InputLabel sx={{ fontSize: 12, color: "#535353", pb: 0.5 }}>{cat.short} *</InputLabel>
            {renderInput(cat.key, cat.straightField)}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

ProjectJobsOvertimeUnionFringeForm.propTypes = {
  formConfig: PropTypes.object.isRequired,
  defaultFormProps: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  addJobPermisison: PropTypes.object,
  prefix: PropTypes.oneOf(["unionOverTime", "unionDoubleTime"]).isRequired,
  title: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  isInvalid: PropTypes.bool,
  onActivate: PropTypes.func
};

export default ProjectJobsOvertimeUnionFringeForm;
